// @flow

import * as React from 'react';
import { connect } from 'react-redux';

import { localize } from '@ta/common.i18n';
import { featureIsEnabled } from '@ta/common.features';
import { trackEventCookie } from '@ta/common.tracking';
import { canRequestLocation } from '@ta/common.geolocation';
import { LoadingSpinner } from '@ta/styleguide.loading';
import type { TypeaheadResult } from '@ta/common.typeahead';
import { component } from '@ta/platform.import';
import type { GeoPillSponsorDataProvider } from '@ta/brand.sponsored-geopill';
import type { GeoScopedTypeAheadProps } from '@ta/brand.geo-scoped-typeahead';

import styles from './GeoPillSearch.less';

const loadingSpinner = (
  <div className={styles.transitionLoadingOverlay}>
    <LoadingSpinner />
  </div>
);

const GeoScopedTypeAhead = component.onUsage<_, GeoScopedTypeAheadProps>(
  () => import('@ta/brand.geo-scoped-typeahead'),
  'default',
  loadingSpinner,
);

type OwnProps = {|
  /** Whether we should be showing the nearby option in typeahead. */
  enableGeoLocation?: boolean,

  /** Prop-drilling */
  latLngUrlTemplate?: ?string,

  /** Prop-drilling */
  geoUrlTemplate?: ?string,

  /** Quick link label for tracking (eg. hotel, attraction, etc.) */
  trackingLabel?: ?string,

  /** additional interaction on selection */
  onSelection?: () => void,

  /** Promise for future geopill sponsor result or null if not enabled */
  sponsorDataProvider?: ?GeoPillSponsorDataProvider,

  /**
   * An optional label to indicate the UI origin of this typeahead. This gets sent to
   * /TypeAheadJson?action=RECORD. If not specified, this is derived from the current page.
   */
  uiOrigin?: string,
|};

type Props = {|
  ...OwnProps,

  /** The geo location ID */
  geo: number,

  /** The search session ID for tracking searches from this typeahead instance */
  searchSessionId: string,

  /** The current servlet name */
  servletName: string | null,

  /** The current viewport category */
  viewportCategory: 'DESKTOP' | 'TABLET' | 'MOBILE',

  /** Whether we show the "Worldwide" result in typeahead */
  enableWorldwide: boolean,

  /** Use smaller font for the typeahead input. Mobile has smaller text */
  smallInputText: boolean,

  scope: number,

  beforeGeoId: number,

  afterGeoId: number,
|};

/**
 * The geo pill search dropdown. This component is responsible for initializing the generic
 * GeoScopedTypeahead component for use in the context of a global nav geopill.
 */
class GeoPillSearch extends React.Component<Props> {
  static defaultProps = {
    enableGeoLocation: true,
    onSelection: undefined,
  };

  onSelection = (event: SyntheticEvent<HTMLElement> | KeyboardEvent, result: TypeaheadResult) => {
    // If the user clicked a URL that we are about to navigate to then track it via a cookie
    if ((result.type === 'url' || result.type === 'recent_url') && result.value) {
      const action = result.type === 'url' ? 'submit' : 'submit_recent';
      const destinationUrl = typeof result.value === 'string' ? result.value : '';
      trackEventCookie({
        module: `${this.props.servletName === 'Home' ? 'Home' : 'Top'}Nav_${this.props
          .servletName || ''}`,
        action,
        context: 'GeoPicker',
        value: 0,
        destinationUrl,
      });
    }

    if (this.props.onSelection) {
      this.props.onSelection();
    }
  };

  render() {
    return (
      <GeoScopedTypeAhead
        placeholder={localize('mobile_discovery_where_to')}
        geoId={this.props.geo}
        searchSessionId={this.props.searchSessionId}
        uiOrigin={this.props.uiOrigin}
        onSelection={this.onSelection}
        enableGeoLocation={this.props.enableGeoLocation && canRequestLocation()}
        enableWorldwide={this.props.enableWorldwide}
        enableGeoNavi={featureIsEnabled('mobile_web_feature_dual_search_geo_navi')}
        latLngUrlTemplate={this.props.latLngUrlTemplate}
        geoUrlTemplate={this.props.geoUrlTemplate}
        trackingLabel={this.props.trackingLabel}
        smallInputText={this.props.smallInputText}
        sponsorDataProvider={this.props.sponsorDataProvider}
        scope={this.props.scope}
        beforeGeoId={this.props.beforeGeoId}
        afterGeoId={this.props.afterGeoId}
      />
    );
  }
}

/**
 * The container for the GeoPillSearch
 */
export default connect<Props, OwnProps, _, _, _, _>((state, ownProps) => {
  const { geo } = state.route;
  const { searchSessionId } = state.tracking || {};
  const {
    initialRelativeUrl,
    initialServletName: servletName,
    device: { viewportCategory },
  } = state.meta;

  let { uiOrigin } = ownProps;
  if (uiOrigin == null) {
    if (!initialRelativeUrl) {
      uiOrigin = 'geopicker';
    } else if (!initialRelativeUrl.substring(1).trim()) {
      uiOrigin = 'Home_geopicker';
    } else {
      uiOrigin = initialRelativeUrl.substring(1).replace(/-.*/, '_geopicker');
    }
  }

  const enableWorldwide = typeof state.route.latitude !== 'undefined' || geo;

  const smallInputText = state.meta.device.viewportCategory === 'MOBILE';

  const beforeGeoId = 1;
  const afterGeoId = 1;
  const scope = 1;

  return {
    geo,
    searchSessionId,
    uiOrigin,
    servletName,
    viewportCategory,
    enableWorldwide,
    smallInputText,
    scope,
    beforeGeoId,
    afterGeoId,
  };
})(GeoPillSearch);
